/**
 * useMT5Detection Hook
 * Polls QBridge to detect MT5 terminal
 *
 * Detection Strategy:
 * 1. Primary: Uses /api/mt5/detect endpoint (provides full account info)
 * 2. Fallback: Uses /api/ea/list endpoint (if MT5 files exist, MT5 is accessible)
 *
 * When using fallback detection, account/broker info will be null but
 * detected=true indicates MT5 files are accessible to QBridge.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
    detectMT5,
    MT5_STATUS,
    POLLING_INTERVALS
} from '../services/qbridge.service';

/**
 * Hook for detecting MT5 terminal via QBridge
 * @param {boolean} enabled - Whether polling is active (typically after QBridge is running)
 * @param {number} interval - Polling interval in ms (default 3000)
 * @returns {{
 *   status: string,
 *   detected: boolean,
 *   account: string | null,
 *   broker: string | null,
 *   balance: number | null,
 *   currency: string | null,
 *   connected: boolean,
 *   isSearching: boolean,
 *   error: string | null,
 *   isFallbackDetection: boolean,
 *   refresh: () => Promise<void>
 * }}
 */
const useMT5Detection = (enabled = false, interval = POLLING_INTERVALS.MT5_DETECTION) => {
    const [status, setStatus] = useState(MT5_STATUS.NOT_DETECTED);
    const [detected, setDetected] = useState(false);
    const [account, setAccount] = useState(null);
    const [broker, setBroker] = useState(null);
    const [balance, setBalance] = useState(null);
    const [currency, setCurrency] = useState(null);
    const [connected, setConnected] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState(null);
    const [isFallbackDetection, setIsFallbackDetection] = useState(false);
    const intervalRef = useRef(null);
    const mountedRef = useRef(true);

    const checkMT5 = useCallback(async () => {
        if (!mountedRef.current) return;

        setIsSearching(true);
        setStatus(MT5_STATUS.SEARCHING);

        const result = await detectMT5();

        if (!mountedRef.current) return;

        setDetected(result.detected);
        setAccount(result.account);
        setBroker(result.broker);
        setBalance(result.balance || null);
        setCurrency(result.currency || null);
        setConnected(result.connected);
        setError(result.error);
        setIsSearching(false);
        // Track if detection was via fallback method (EA file list)
        setIsFallbackDetection(result._fallback || false);

        if (result.detected) {
            setStatus(MT5_STATUS.DETECTED);
        } else if (result.error) {
            setStatus(MT5_STATUS.ERROR);
        } else {
            setStatus(MT5_STATUS.NOT_DETECTED);
        }

        return result;
    }, []);

    // Polling setup
    useEffect(() => {
        mountedRef.current = true;

        if (enabled) {
            // Initial check immediately
            checkMT5();

            // Set up polling
            intervalRef.current = setInterval(checkMT5, interval);
        }

        return () => {
            mountedRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [enabled, interval, checkMT5]);

    // Stop polling when MT5 is detected
    useEffect(() => {
        if (detected && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, [detected]);

    const refresh = useCallback(async () => {
        return checkMT5();
    }, [checkMT5]);

    return {
        status,
        detected,
        account,
        broker,
        balance,
        currency,
        connected,
        isSearching,
        error,
        isFallbackDetection,  // True if detected via EA file list (account info unavailable)
        refresh
    };
};

export default useMT5Detection;
